import {useId} from "react";
import {useLocale} from "i18n";
import {ADVERTISED_TREATMENTS} from "./advertisedTreatments";
import type {SponsorAvailabilityDto} from "sponsor/SponsorApi";
import {AdvertisingHero} from "./AdvertisingHero";
import {AdvertisingCloser} from "./AdvertisingCloser";
import {AdvertisingTreatments} from "./AdvertisingTreatments";
import {ADVERTISING_STEP_ART, AdvertisingStepArt} from "./AdvertisingStepArt";
import {useAdvertisingAvailability} from "./useAdvertisingAvailability";
import {advertisingCopy, type AdvertisingListCopy, type AdvertisingTermCopy} from "./copy/advertising";
import {
	ListGlyph,
	ListHeading,
	ListItem,
	ListPair,
	PageRoot,
	PointLead,
	PointList,
	SectionBand,
	SectionHead,
	SectionHeading,
	SectionLede,
	StepArtFrame,
	StepItem,
	StepLine,
	StepNumber,
	StepsList,
	StepTitle,
	TermsCaption,
	TermsCell,
	TermsFigure,
	TermsGrid,
	TermsLiveTag,
	TermsStrip,
	TermsUnit,
} from "./AdvertisingContent.styles";

/**
 * Public, crawlable **Advertising** landing page (`/advertising`), rebuilt in ui#1861.
 *
 * It replaces roughly 900 words of prose with the thing the words described. Six bands, top to bottom:
 * the hero and its picture of a download page; the terms of the offer; the four booking steps; the
 * treatments in their two categories, each previewed by the real renderer; two short lists; and the
 * closer with a fortnight of live availability beside it.
 *
 * ## One read, two bands
 *
 * `GET /sponsor/availability` is fetched HERE, once, and handed to the two bands that need it. Reading
 * it in each would put two requests on a marketing page and — worse — would let the strip and the
 * closing row disagree about what the server said, since the two responses need not be identical.
 *
 * The read is anonymous and deferred to an effect, so nothing about it happens during the SSG
 * pre-render. That is what keeps operator policy out of a file the CDN caches, and what keeps the static
 * HTML from describing figures it does not contain — see {@link useAdvertisingAvailability}.
 *
 * ## No dialog surface any more
 *
 * `AdvertisingBody` is gone (owner, 2026-09-02: "it can open in a new page"). The Resources menu entry
 * is now plain navigation to this route in both auth states, so there is no second surface this page's
 * bands have to be safe inside — which is why the hero, the gallery and the closer can be full-width
 * furniture rather than things a modal sheet has to survive.
 */
export function AdvertisingContent() {
	const availability = useAdvertisingAvailability();
	return (
		<PageRoot>
			<AdvertisingHero/>
			<AdvertisingTerms availability={availability}/>
			<AdvertisingSteps/>
			<AdvertisingTreatments/>
			<AdvertisingLists/>
			<AdvertisingCloser availability={availability}/>
		</PageRoot>
	);
}

/**
 * The terms strip: what a booking IS, in three figures.
 *
 * Not one of the three is written in the copy module. Two are facts about the product — a day holds one
 * advertiser, and `ADVERTISED_TREATMENTS` is however long it is — and the third is operator policy read
 * live. The treatment count in particular is derived rather than typed because a restated count has
 * already been wrong twice: the prose said "three" through two widenings before ui#624 added the
 * coverflow, and the copy said "six" until kitecorp/transferify-ui#1870, by which time the wallpapers
 * had made the catalogue ten. It counts what a buyer can actually see and BOOK, which is neither
 * category list on its own — `advertisedTreatments.ts` states the two conditions.
 *
 * The run-length cell is ABSENT until the read returns, and stays absent if it fails. No spinner and no
 * zero: "0 days per run" is a sentence that means the product is closed, and a placeholder that resolves
 * into a different number is the layout jump this page defers the read to avoid.
 */
function AdvertisingTerms({availability}: {availability: SponsorAvailabilityDto | null}) {
	const {locale} = useLocale();
	const {terms} = advertisingCopy[locale];
	return (
		<TermsStrip aria-label={terms.aria}>
			<TermsGrid>
				<TermsFact figure={ADVERTISERS_PER_DAY} copy={terms.advertisers}/>
				<TermsFact figure={ADVERTISED_TREATMENTS.length} copy={terms.treatments}/>
				{availability !== null && (
					<TermsFact figure={availability.maxDays} copy={terms.runLength} liveLabel={terms.liveLabel}/>
				)}
			</TermsGrid>
		</TermsStrip>
	);
}

/**
 * One cell.
 *
 * `liveLabel` marks the cells whose figure came from the live read. Worth distinguishing: the first two
 * are true of the product and will be true tomorrow, while the run length can change between two visits,
 * and a reader who cannot tell them apart has no way to know which to trust in a screenshot.
 */
function TermsFact(
	{figure, copy, liveLabel}: {figure: number; copy: AdvertisingTermCopy; liveLabel?: string},
) {
	return (
		<TermsCell>
			{/* The space is EXPLICIT. JSX drops whitespace that spans a newline, so without it the
			    figure and its unit concatenate into one text run — "7days per run" is what a screen
			    reader would read, and what any text assertion would have to match. */}
			<TermsFigure>
				{figure}{" "}
				<TermsUnit>{copy.unit(figure)}</TermsUnit>
			</TermsFigure>
			<TermsCaption>{copy.caption}</TermsCaption>
			{liveLabel !== undefined && <TermsLiveTag>{liveLabel}</TermsLiveTag>}
		</TermsCell>
	);
}

/**
 * Exclusivity, as a number.
 *
 * Named rather than inlined as `1`, because it is a claim about the product ("no rotation, no share of
 * voice") and not a formatting decision. It is what availability enforces: one booking per day, so a day
 * is free or it is not (`sponsor/sponsorCalendar.ts`).
 */
const ADVERTISERS_PER_DAY = 1;

/**
 * The four booking steps.
 *
 * An `<ol>`, because the order is the product: this is the booking page's own sequence, and the numbers
 * are visible for the same reason. The illustrations carry nothing the text does not, so they are
 * `aria-hidden` — see {@link AdvertisingStepArt}.
 */
function AdvertisingSteps() {
	const {locale} = useLocale();
	const {steps} = advertisingCopy[locale];
	const headingId = useId();
	return (
		<SectionBand aria-labelledby={headingId}>
			<SectionHead>
				<SectionHeading id={headingId}>{steps.heading}</SectionHeading>
				<SectionLede>{steps.lede}</SectionLede>
			</SectionHead>
			<StepsList>
				{steps.steps.map((step, index) => (
					<StepItem key={step.title}>
						<StepArtFrame>
							{/* Paired by POSITION with the copy above, which is safe because both lists are
							    fixed-length and ordered by the same booking sequence. A drawing keyed by title
							    would break on a reword; keyed by index it follows one. */}
							<AdvertisingStepArt step={ADVERTISING_STEP_ART[index]}/>
						</StepArtFrame>
						<StepNumber>{steps.stepLabel(index + 1)}</StepNumber>
						<StepTitle>{step.title}</StepTitle>
						<StepLine>{step.line}</StepLine>
					</StepItem>
				))}
			</StepsList>
		</SectionBand>
	);
}

/**
 * The two short lists: who is on the other side, and what we promise.
 *
 * The band carries no heading of its own — the two list headings ARE its content, and a third heading
 * above them would be a chapter title for two paragraphs. Each list is a real `<ul>`, so a screen reader
 * hears three items rather than a run of sentences.
 */
function AdvertisingLists() {
	const {locale} = useLocale();
	const {audience, promises} = advertisingCopy[locale];
	return (
		<SectionBand>
			<ListPair>
				<PointColumn copy={audience} signal={false}/>
				<PointColumn copy={promises} signal={true}/>
			</ListPair>
		</SectionBand>
	);
}

function PointColumn({copy, signal}: {copy: AdvertisingListCopy; signal: boolean}) {
	const headingId = useId();
	return (
		<div>
			<ListHeading id={headingId}>{copy.heading}</ListHeading>
			<PointList aria-labelledby={headingId}>
				{copy.points.map(point => (
					<ListItem key={point.lead}>
						{/* Decorative: the glyph repeats the list's own character and adds nothing a reader
						    without it would miss. The two tints are what separate a fact from a commitment. */}
						<ListGlyph $signal={signal} aria-hidden="true">
							{signal ? <CheckGlyph/> : <DotGlyph/>}
						</ListGlyph>
						<span>
							{/* Explicit, for the reason the terms cell states: a block element and the run after
							    it concatenate in `textContent` unless the space is written out. */}
							<PointLead>{point.lead}</PointLead>{" "}
							{point.rest}
						</span>
					</ListItem>
				))}
			</PointList>
		</div>
	);
}

/** A tick, for a promise. */
function CheckGlyph() {
	return (
		<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
			strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
			<path d="M5 12l5 5L19 7"/>
		</svg>
	);
}

/** A filled marker, for a statement of fact. Deliberately not a tick: a fact is not a commitment. */
function DotGlyph() {
	return (
		<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
			strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
			<circle cx="12" cy="12" r="4.5" fill="currentColor" stroke="none"/>
			<circle cx="12" cy="12" r="9"/>
		</svg>
	);
}
